<template>
  <div v-if="preparedOrder && checkout">
    <v-row density="comfortable">
      <v-col cols="12" md="6" lg="">
        <order-address-and-contact-data
          :address-type="AddressType.BILLING"
          :address-selection="checkout.billingAddress"
          :address="preparedOrder.billingAddress"
          :contact-data="preparedOrder.billingContactData || undefined"
          :events="billingEvents"
          @select-address="selectBillingAddress"
          @update-contact-data="updateBillingContactData"
        />
      </v-col>
      <v-col cols="12" md="6" lg="">
        <order-address-and-contact-data
          :address-type="AddressType.DELIVERY"
          :address-selection="checkout.deliveryAddress"
          :address="preparedOrder.deliveryAddress"
          :contact-data="preparedOrder.deliveryContactData || undefined"
          :events="shippingEvents"
          @select-address="selectDeliveryAddress"
          @update-contact-data="updateDeliveryContactData"
        />
      </v-col>
      <v-col cols="12" sm="">
        <order-comment
          :events="commentEvents"
          :max-length="orderingConditions.maxCommentLength"
          :order-comment="preparedOrder.orderComment || undefined"
          :partial-shipment-enabled="orderingConditions.isPartialShipmentEnabled"
          :partial-shipment-select="preparedOrder.usePartialShipment"
          editable
          @order-comment-changed="orderCommentChanged"
          @partial-shipment-selection-changed="partialShipmentSelectionChanged"
        />
      </v-col>
      <v-col v-if="portalSettings.showPaymentMethodSelection" cols="12" sm="">
        <mobex-payment-method v-model="paymentMethod" />
      </v-col>
    </v-row>
    <v-row density="comfortable">
      <v-col>
        <order-numbers
          :customer-number="checkout.customerNumber"
          :customer-order-id-required="orderingConditions.customerOrderIdRequired"
          :events="orderNumberEvents"
          :order-number="preparedOrder.orderNumber || undefined"
          :show-order-number="isQuote || portalSettings.isCustomOrderNumberEnabled"
          :is-custom-order-number-enabled="portalSettings.isCustomOrderNumberEnabled"
          :show-customer-order-id="orderingConditions.showCustomerOrderId"
          :customer-order-id="preparedOrder.customerOrderId || undefined"
          @customer-order-id-changed="customerOrderIdChanged"
          @order-number-changed="orderNumberChanged"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import useCart from '@/modules/cart/useCart'
import MobexPaymentMethod from '@/modules/order/components/MobexPaymentMethod.vue'
import OrderAddressAndContactData from '@/modules/order/components/OrderAddressAndContactData.vue'
import OrderComment from '@/modules/order/components/OrderComment.vue'
import OrderNumbers from '@/modules/order/components/OrderNumbers.vue'
import useCheckout from '@/modules/order/useCheckout'
import useOrderingConditions from '@/modules/portalSettings/useOrderingConditions'
import usePortalSettings from '@/modules/portalSettings/usePortalSettings'
import { AddressChangeEvents } from '@/modules/tracking/events/addresses'
import { CommentChangeEvents, OrderNumberEvents } from '@/modules/tracking/events/checkout'
import { AddressType, ContactData, ShopAddress } from '@/types/index'
import { storeToRefs } from 'pinia'
import { PropType, ref, watch } from 'vue'

defineProps({
  shippingEvents: {
    type: Object as PropType<AddressChangeEvents>,
    default: undefined
  },
  billingEvents: {
    type: Object as PropType<AddressChangeEvents>,
    default: undefined
  },
  commentEvents: {
    type: Object as PropType<CommentChangeEvents>,
    default: undefined
  },
  orderNumberEvents: {
    type: Object as PropType<OrderNumberEvents>,
    default: undefined
  },
  isQuote: {
    type: Boolean,
    default: false
  }
})

const { portalSettings } = storeToRefs(usePortalSettings())
const { orderingConditions } = storeToRefs(useOrderingConditions())
const { checkout, preparedOrder } = storeToRefs(useCheckout())

const paymentMethod = ref(preparedOrder.value?.paymentMethod || undefined)
watch(paymentMethod, (newValue) => {
  preparedOrder.value!.paymentMethod = newValue
})

const selectBillingAddress = (address: ShopAddress) => {
  useCheckout().setBillingAddress(address)
}

const selectDeliveryAddress = (address: ShopAddress) => {
  useCheckout().setDeliveryAddress(address)
  useCart().changeVatCountry(address.iso3country)
}

const updateBillingContactData = (updatedContactData?: ContactData) => {
  useCheckout().setBillingContactData(updatedContactData)
}

const updateDeliveryContactData = (updatedContactData?: ContactData) => {
  useCheckout().setDeliveryContactData(updatedContactData)
}

const partialShipmentSelectionChanged = (selection: boolean) => {
  useCheckout().setPartialShipmentSelection(selection)
}

const orderNumberChanged = (orderNumber: string) => {
  useCheckout().setOrderNumber(orderNumber)
}

const customerOrderIdChanged = (customerOrderId: string) => {
  useCheckout().setCustomerOrderId(customerOrderId)
}

const orderCommentChanged = (comment: string) => {
  useCheckout().setOrderComment(comment)
}
</script>
